/**
 * Estructura del curso Open Water Diver (PADI)
 * Modelo de gamificación tipo Duolingo / Age of Empires
 */
import type { GameId } from '../stores/gamificationStore'

export interface CourseObjective {
  id: string
  title: string
  description: string
  emoji: string
  /** Tipo de actividad para completar el objetivo */
  type: 'quiz' | 'game' | 'lesson'
  /** Para type: 'game' - qué juego debe completar */
  gameId?: GameId
  gameLevel?: number
  /** Para type: 'quiz' - topic o module del quiz */
  quizTopic?: string
  quizModuleId?: string
  /** Requisitos: mínimo score o nivel del juego */
  minScore?: number
  minCorrectAnswers?: number
  /** XP que otorga al completar */
  xpReward: number
}

export interface CourseModule {
  id: string
  title: string
  description: string
  emoji: string
  /** Posición en el mapa (para layout tipo RTS) */
  position: { x: number; y: number }
  /** Objetivos/lecciones del módulo */
  objectives: CourseObjective[]
  /** Módulo anterior requerido (para desbloquear) */
  requiresModuleId?: string
}

export interface Course {
  id: string
  name: string
  description: string
  emoji: string
  color: string
  gradient: string
  modules: CourseModule[]
}

export const openWaterCourse: Course = {
  id: 'open-water',
  name: 'Open Water Diver',
  description: 'Certificación de buceo recreativo - aprende a explorar el mundo submarino',
  emoji: '🤿',
  color: 'cyan',
  gradient: 'from-cyan-500 to-blue-600',
  modules: [
    {
      id: 'mod1',
      title: 'El mundo submarino',
      description: 'Introducción: equipo, señales y fundamentos',
      emoji: '🌊',
      position: { x: 20, y: 15 },
      objectives: [
        { id: 'ow1-q1', title: 'Quiz: Fundamentos', description: 'Responde 4/5 correctas sobre equipo y gases', emoji: '🧠', type: 'quiz', quizModuleId: 'mod1', minCorrectAnswers: 4, xpReward: 50 },
        { id: 'ow1-g1', title: 'Memoji: Equipo', description: 'Memoriza las parejas del equipo de buceo', emoji: '🧠', type: 'game', gameId: 'memoji', gameLevel: 0, minScore: 500, xpReward: 60 },
        { id: 'ow1-g2', title: 'HitMoji: Señales', description: 'Combina las señales de buceo', emoji: '👋', type: 'game', gameId: 'hitmoji', gameLevel: 0, minScore: 300, xpReward: 70 },
      ],
    },
    {
      id: 'mod2',
      title: 'Adaptación al agua',
      description: 'Presión, visión, sonido y temperatura',
      emoji: '👁️',
      position: { x: 50, y: 25 },
      requiresModuleId: 'mod1',
      objectives: [
        { id: 'ow2-q1', title: 'Quiz: Física del buceo', description: '4/5 sobre presión y refracción', emoji: '📐', type: 'quiz', quizModuleId: 'mod2', minCorrectAnswers: 4, xpReward: 60 },
        { id: 'ow2-g1', title: 'FindMoji: Vida marina', description: 'Encuentra las criaturas del océano', emoji: '🐠', type: 'game', gameId: 'findmoji', gameLevel: 0, minScore: 400, xpReward: 65 },
        { id: 'ow2-g2', title: 'QuizMoji: Colores', description: '¿Qué colores desaparecen primero?', emoji: '🌈', type: 'quiz', quizModuleId: 'mod2', minCorrectAnswers: 3, xpReward: 55 },
      ],
    },
    {
      id: 'mod3',
      title: 'Planificación',
      description: 'Tablas, plan buddy y emergencias',
      emoji: '📋',
      position: { x: 80, y: 40 },
      requiresModuleId: 'mod2',
      objectives: [
        { id: 'ow3-q1', title: 'Quiz: Descompresión', description: 'Planificación y ascenso seguro', emoji: '⌚', type: 'quiz', quizModuleId: 'mod3', minCorrectAnswers: 4, xpReward: 70 },
        { id: 'ow3-g1', title: 'Evoji: Evolución del buceo', description: 'Del snorkel al equipo completo', emoji: '🧬', type: 'game', gameId: 'evoji', gameLevel: 0, minScore: 512, xpReward: 80 },
        { id: 'ow3-g2', title: 'Memoji: Procedimientos', description: 'Memoriza protocolos de emergencia', emoji: '🆘', type: 'game', gameId: 'memoji', gameLevel: 2, minScore: 600, xpReward: 75 },
      ],
    },
    {
      id: 'mod4',
      title: 'Habilidades',
      description: 'Flotabilidad, equalización y navegación',
      emoji: '🦺',
      position: { x: 60, y: 65 },
      requiresModuleId: 'mod3',
      objectives: [
        { id: 'ow4-q1', title: 'Quiz: Técnicas', description: 'BCD, igualación y brújula', emoji: '🧭', type: 'quiz', quizModuleId: 'mod4', minCorrectAnswers: 4, xpReward: 75 },
        { id: 'ow4-g1', title: 'HitMoji: Niveles', description: 'Practica tu reflejo con niveles de buceo', emoji: '💥', type: 'game', gameId: 'hitmoji', gameLevel: 3, minScore: 400, xpReward: 80 },
        { id: 'ow4-g2', title: 'Timeline: Profundidades', description: 'Ordena por profundidad máxima', emoji: '📏', type: 'game', gameId: 'timeline', minScore: 800, xpReward: 85 },
      ],
    },
    {
      id: 'mod5',
      title: 'Buceo responsable',
      description: 'Conservación y seguridad con compañero',
      emoji: '🐚',
      position: { x: 30, y: 75 },
      requiresModuleId: 'mod4',
      objectives: [
        { id: 'ow5-q1', title: 'Quiz final: Certificación', description: '5/5 para tu certificación Open Water', emoji: '🎓', type: 'quiz', quizModuleId: 'mod5', minCorrectAnswers: 5, xpReward: 100 },
        { id: 'ow5-g1', title: 'QuizMoji: Responsabilidad', description: 'Último desafío de conocimiento', emoji: '🤝', type: 'quiz', quizModuleId: 'mod5', minCorrectAnswers: 4, xpReward: 90 },
      ],
    },
  ],
}

/** Todos los objetivos del curso en orden */
export const getAllObjectives = (): CourseObjective[] =>
  openWaterCourse.modules.flatMap(m => m.objectives)

export const getObjectiveById = (id: string): CourseObjective | undefined =>
  getAllObjectives().find(o => o.id === id)

export const getModuleById = (id: string): CourseModule | undefined =>
  openWaterCourse.modules.find(m => m.id === id)
